import { Box, Button, Typography } from '@mui/material'
import CardTAG from '../../components/CardTAG'
import MainLayout from '../../components/MainLayout'
import ModalTAG from '../../components/ModalTAG'
import AddUserForm from '../../components/forms/AddUserForm'
import AddIcon from '@mui/icons-material/Add'
import TableTAG from '../../components/TableTAG'
import { useEffect, useState } from 'react'

import SearchInput from '../../components/SearchInput'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../store'
import {
  resetUserPassword,
  deactivateUser,
  addNewUser,
  getOrganizationUsers,
  patchUser,
  patchUserProfile,
} from '../../store/users/usersSlice'
import { INewUser } from '../../components/forms/AddUserForm/types'
import DividerTAG from '../../components/DividerTAG'
import { deselectCase } from '../../store/reports/reportsSlice'
import constants from './constants'
import usersSelectors from '../../store/users/usersSelectors'
import authSelectors from '../../store/auth/authSelectors'
import { IAuthJWT } from '../../config/axios'
import jwtDecode from 'jwt-decode'
import { getUserDetails } from '../../store/auth/authSlice'
import ShowForUserRoles from '../../components/ShowForUserTypes'
import { userRoles } from '../../store/auth/userTypes'
import { roleMappings } from '../../store/auth/userTypes';
import useDebounce from '../../hooks/useDebounce'
import useAutoLogout from '../../hooks/useAutoLogout'

import { success, error } from '../../utils/alertUtils';



const UsersPage: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchUser] = useState<string>('')
  const [userToUpdate, setUserToUpdate] = useState<any>()
  const [updatingUserOldValues, setUpdatingUserOldValues] = useState<any>()
  const debouncedSearchValue = useDebounce(searchValue, 500)
  const autoLogout = useAutoLogout()


  const organizationUsers = useSelector(usersSelectors.organizationUsers)

  const dispatch = useDispatch<AppDispatch>()
  const getUsers = async () => {
    dispatch(getOrganizationUsers({ searchValue, excludeLoggedUser: false, onlyAssignable: false}))
  }
  useEffect(() => {
    dispatch(deselectCase({ value: undefined }))
    dispatch(getOrganizationUsers({ searchValue, excludeLoggedUser: false, onlyAssignable: false}))
    const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
    dispatch(getUserDetails({ userId: tokenData.user_id }))
  }, [])

  useEffect(() => {
    getUsers()
  }, [debouncedSearchValue])

  const onSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchUser(e.target.value)
  }

  const resetPassword = (userId: number) => {
    dispatch(resetUserPassword({ userId })).then((res) => {
      success('Password reset email sent!')
    }).catch(() => {
      error('Reset password failed. Please try again.')
    })
  }

  const userDeactivation = (userId: number) => {
    dispatch(deactivateUser({ userId }))
  }

  const editUserInfo = (userData: any) => {
    const { user, ...userprofile } = userData
    const formatedUserData = {
      user,
      userprofile: {
        ...userprofile,
        roles: userprofile?.roles?.map((item: { text: any }) => item?.text),
      },
    }
    setUserToUpdate(formatedUserData)
    setUpdatingUserOldValues(formatedUserData)
  }

  const copyTextToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value)
    success('Email copied to clipboard!')
  }
  const userOrganization = useSelector(authSelectors.userOrganization)

  const onSubmit = (data: INewUser) => {
    data.user.username = data?.user?.email
    data.user.is_active = 1 // Active by default
    const newUser: INewUser = {
      user: data?.user,
      userprofile: {
        organization: userOrganization as unknown as string,
        roles: [...(data.userprofile?.roles as number[])],
        profile_pic: data?.userprofile?.profile_pic,
      },
    }
    dispatch(addNewUser({ newUser }))
    setOpen(false)
  }

  const onUpdateUser = (data: any) => {
    function getChangedProperties(oldUser: any, newUser: any) {
      const changedProperties: { [key: string]: any } = {}

      for (const key in newUser) {
        if (oldUser[key] !== newUser[key]) {
          changedProperties[key] = newUser[key]
        }
      }

      return changedProperties
    }
    const { user, ...userProfile } = data
    const { user: userOld, ...userProfileOld } = updatingUserOldValues
    const changedPropertiesUser = getChangedProperties(userOld, user)

    const changedPropertiesUserProfile = getChangedProperties(
        userProfileOld?.userprofile,
        userProfile?.userprofile,
    )

    changedPropertiesUser.userprofile = {};
    changedPropertiesUser.userprofile.roles = [...(data.userprofile?.roles as number[])];

    // console.log('changedPropertiesUser', changedPropertiesUser)
    dispatch(patchUser({ newUser: changedPropertiesUser, userId: data?.user?.id as number }))
    // console.log('changedPropertiesUserProfile', changedPropertiesUserProfile)

    //maping for properly set roles on backend
    if (changedPropertiesUserProfile != null && Object.keys(changedPropertiesUserProfile).length > 0) {


      // Map numeric roles to their corresponding names
      const rolesNames = data.userprofile.roles.map((role: number) => {
        return Object.keys(roleMappings).find((key) => roleMappings[key as userRoles] === role) as userRoles;
      });

      dispatch(
          patchUserProfile({
            userData: { ...changedPropertiesUserProfile, roles: rolesNames },
            userId: data?.userprofile?.id as number,
          })
      );
    }

    setUserToUpdate(false);
  };

  return (
      <>
        <MainLayout childrenSx={{ paddingRight: 0 }}>
          <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                overflowY: 'auto',
              }}
          >
            <ShowForUserRoles message='Admin restricted view' roles={[userRoles.ADMIN]}>
              <CardTAG title='Users'>
                <Box sx={constants.usersPageMainBoxStyle}>
                  <Box sx={constants.usersPageActionsBox}>
                    <Button
                        variant='submitFormButton'
                        startIcon={<AddIcon />}
                        sx={{ width: { xs: '100%', md: '30%' } }}
                        onClick={() => {
                          setOpen(true)
                        }}
                    >
                      <Typography variant='bodyBold'>Invite New User</Typography>
                    </Button>
                    <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'end', flex: 1 }}>
                      <SearchInput
                          label='User Search'
                          value={searchValue}
                          onValueChange={onSearchValueChange}
                          placeholder='Type here to filter results'
                      />
                    </Box>
                  </Box>
                  <DividerTAG />
                  {!!organizationUsers.length && (
                      <TableTAG
                          columns={constants.getColumns(
                              resetPassword,
                              copyTextToClipboard,
                              userDeactivation,
                              editUserInfo,
                          )}
                          rows={organizationUsers}
                          pagination={false}
                      />
                  )}
                </Box>
              </CardTAG>
            </ShowForUserRoles>
          </Box>
        </MainLayout>
        <ModalTAG open={open} style={{ maxWidth: '600px', width: { xs: '95%', md: '60vw' } }}>
          <AddUserForm onSubmit={onSubmit} onClose={(value: boolean) => setOpen(value)}></AddUserForm>
        </ModalTAG>
        <ModalTAG
            open={!!userToUpdate}
            style={{ maxWidth: '600px', width: { xs: '95%', md: '60vw' } }}
        >
          <AddUserForm
              onSubmit={onUpdateUser}
              onClose={(value: boolean) => setUserToUpdate(false)}
              userData={userToUpdate}
          ></AddUserForm>
        </ModalTAG>
      </>
  )
}

export default UsersPage
